// src/views/parent/PAAbonnements.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import { useParentDrawer } from "../../context/ParentDrawerContext";
import Swal from "sweetalert2";

// ⚠️ Actions désactivées pour le moment : le renouvellement/la résiliation
// sont simulés côté backend (le parent pourrait renouveler à volonté sans
// contrôle réel). On grise les boutons en attendant l'implémentation finale.
// Pour réactiver : repasser ACTIONS_ENABLED à true.
const ACTIONS_ENABLED = false;

export default function PAAbonnements({ slug }) {
  const navigate = useNavigate();
  const { openDrawer } = useParentDrawer();
  const { getAbonnementsParent, renouvelerAbonnement, resilierAbonnement } = useParentService();

  const [abonnements, setAbonnements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // 🛡️ Réf anti-boucle / anti-StrictMode
  const hasFetchedRef = useRef(false);

  // ─── CHARGEMENT ──────────────────────────────────────────────────────
  const loadAbonnements = useCallback(async () => {
    if (!slug) {
      setError("Identifiant parent manquant.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getAbonnementsParent(slug);
      setAbonnements(data || []);
    } catch (err) {
      setError("Impossible de charger les abonnements.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadAbonnements();
  }, [loadAbonnements]);

  // ─── HELPERS ─────────────────────────────────────────────────────────
  const handleRenouveler = async (abonnement) => {
    if (!ACTIONS_ENABLED) {
      Swal.fire({
        title: "Bientôt disponible",
        text: "Le renouvellement en ligne sera bientôt activé. Contactez l'administration pour le moment.",
        icon: "info",
        confirmButtonColor: "var(--pr)",
      });
      return;
    }

    const isActive = abonnement.is_active ?? false;
    const offreNom = abonnement.offre?.nom || "abonnement";

    const result = await Swal.fire({
      title: "Renouvellement",
      text: isActive
        ? `Voulez-vous renouveler votre offre "${offreNom}" ?`
        : `Votre abonnement est expiré. Voulez-vous le réactiver ?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Renouveler",
      cancelButtonText: "Annuler",
      confirmButtonColor: "#1B4F80",
      cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    setActionLoading(abonnement.id);
    try {
      await renouvelerAbonnement(slug, abonnement.id);

      await Swal.fire({
        title: "Succès",
        text: "Abonnement renouvelé avec succès !",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        position: "top-right",
        toast: true,
      });

      await loadAbonnements();
    } catch (err) {
      await Swal.fire({
        title: "Erreur",
        text: "Erreur lors du renouvellement",
        icon: "error",
        confirmButtonColor: "#1B4F80",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleResilier = async (abonnement) => {
    if (!ACTIONS_ENABLED) {
      Swal.fire({
        title: "Bientôt disponible",
        text: "La résiliation en ligne sera bientôt activée. Contactez l'administration pour le moment.",
        icon: "info",
        confirmButtonColor: "var(--pr)",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Résiliation",
      text: `Voulez-vous vraiment résilier votre abonnement "${abonnement.offre?.nom || 'abonnement'}" ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Résilier",
      cancelButtonText: "Annuler",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    setActionLoading(abonnement.id);
    try {
      await resilierAbonnement(slug, abonnement.id);

      await Swal.fire({
        title: "Résiliation effectuée",
        text: "Votre abonnement a été résilié.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        position: "top-right",
        toast: true,
      });

      await loadAbonnements();
    } catch (err) {
      await Swal.fire({
        title: "Erreur",
        text: "Impossible de résilier l'abonnement",
        icon: "error",
        confirmButtonColor: "#1B4F80",
      });
    } finally {
      setActionLoading(null);
    }
  };

  // ─── RENDU ───────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; }

        .abonnements-page {
          min-height: 100vh;
          background: var(--s2);
        }

        .abonnements-header {
          background: linear-gradient(135deg, var(--pr-dk), var(--pr));
          padding: 52px 16px 40px;
          text-align: center;
          border-radius: 0 0 40px 40px;
          position: relative;
          box-shadow: 0 4px 15px rgba(0,0,0,0.15);
          overflow: hidden;
        }

        .abonnements-header::after {
          content: "";
          position: absolute;
          left: 20%;
          right: 20%;
          bottom: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, var(--or), transparent);
        }

        .abonnements-header .menu-btn {
          position: absolute;
          top: 44px;
          left: 16px;
          background: rgba(255,255,255,0.2);
          border: none;
          border-radius: 12px;
          padding: 8px 10px;
          cursor: pointer;
          color: #fff;
          font-size: 20px;
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s;
        }

        .abonnements-header .menu-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .abonnements-header .icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 10px;
        }

        .abonnements-header .icon-wrapper i {
          font-size: 24px;
          color: #fff;
        }

        .abonnements-header h1 {
          font-family: 'Poppins', sans-serif;
          color: #fff;
          font-size: 22px;
          font-weight: 700;
          margin: 0;
        }

        .abonnements-header p {
          font-family: 'Inter', sans-serif;
          color: rgba(255,255,255,0.75);
          font-size: 14px;
          margin: 8px 0 0;
        }

        .abonnements-list {
          padding: 16px;
        }

        /* ─── LOADER intégré (header conservé) ─── */
        .abonnements-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          gap: 14px;
        }

        .abonnements-loading .spinner {
          width: 44px;
          height: 44px;
          border: 4px solid var(--pr-bg, #e5eef7);
          border-top-color: var(--pr);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .abonnements-loading span {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* ─── CARTE D'ERREUR (identique PANotifications) ─── */
        .abonnements-error {
          background: #fff;
          border-radius: 20px;
          padding: 40px 20px;
          text-align: center;
          box-shadow: 0 4px 18px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          margin: 16px;
        }

        .abonnements-error i.alert-icon {
          font-size: 48px;
          color: var(--rd);
        }

        .abonnements-error h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 17px;
          font-weight: 600;
          color: var(--t);
          margin: 4px 0 0;
        }

        .abonnements-error p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0 0 12px;
        }

        .abonnements-error .actions-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .abonnements-error .btn {
          border: none;
          border-radius: 30px;
          padding: 10px 22px;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #fff;
          background: var(--pr);
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: opacity 0.2s;
        }

        .abonnements-error .btn:hover { opacity: 0.9; }

        .abonnements-error .btn.btn-secondary {
          background: var(--b2);
          color: var(--t);
        }

        /* ─── CARTES ABONNEMENT ─── */
        .abonnement-card {
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.06);
          margin-bottom: 14px;
          overflow: hidden;
          animation: fadeUp 0.4s ease both;
        }

        .abonnement-card .card-header {
          padding: 16px 18px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 8px;
        }

        .abonnement-card .card-header .left {
          flex: 1;
          min-width: 0;
        }

        .abonnement-card .card-header .title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: 'Poppins', sans-serif;
          font-size: 15px;
          font-weight: 700;
          color: var(--t);
          margin: 0 0 4px;
        }

        .abonnement-card .card-header .title i {
          color: var(--pr);
        }

        .abonnement-card .card-header .eleve-info {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 4px 8px;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
        }

        .abonnement-card .card-header .eleve-info i {
          font-size: 13px;
          color: var(--pr);
        }

        .abonnement-card .card-header .eleve-info .classe-badge {
          background: var(--pr-bg);
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          color: var(--pr);
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .abonnement-card .card-header .status {
          padding: 4px 12px;
          border-radius: 20px;
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          font-weight: 700;
          color: #fff;
          flex-shrink: 0;
        }

        .abonnement-card .card-header .status.active {
          background: var(--gn);
        }

        .abonnement-card .card-header .status.expired {
          background: var(--rd);
        }

        .abonnement-card .card-divider {
          border: none;
          border-top: 1px solid var(--b);
          margin: 0;
        }

        .abonnement-card .card-body {
          padding: 16px 18px;
        }

        .abonnement-card .card-body .dates {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 12px;
        }

        .abonnement-card .card-body .dates .item {
          display: flex;
          align-items: center;
          gap: 4px;
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          color: var(--m);
        }

        .abonnement-card .card-body .dates .item i {
          font-size: 13px;
        }

        .abonnement-card .card-body .description {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0 0 8px;
        }

        .abonnement-card .card-body .meta {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 500;
          color: var(--t);
          margin-bottom: 14px;
        }

        .abonnement-card .card-body .expired-msg {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: #fff7ed;
          border: 1px solid #fed7aa;
          border-radius: 10px;
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          color: #ea580c;
          margin-bottom: 14px;
        }

        .abonnement-card .card-body .disabled-note {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: var(--s2);
          border: 1px dashed var(--b2);
          border-radius: 10px;
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          color: var(--h);
          margin-bottom: 10px;
        }

        .abonnement-card .card-body .actions {
          display: flex;
          gap: 8px;
        }

        .abonnement-card .card-body .actions .btn {
          flex: 1;
          padding: 10px;
          border: none;
          border-radius: 30px;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 700;
          color: #fff;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .abonnement-card .card-body .actions .btn-renew {
          background: var(--pr);
        }

        .abonnement-card .card-body .actions .btn-renew:hover {
          opacity: 0.9;
        }

        .abonnement-card .card-body .actions .btn-resilier {
          background: var(--rd);
        }

        .abonnement-card .card-body .actions .btn-resilier:hover {
          opacity: 0.9;
        }

        .abonnement-card .card-body .actions .btn:disabled {
          background: var(--b2);
          color: var(--h);
          cursor: not-allowed;
          opacity: 1;
        }

        .abonnement-card .card-body .actions .btn:disabled:hover {
          opacity: 1;
        }

        /* Mini spinner dans les boutons en cours d'action */
        .btn .btn-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        /* ─── ÉTAT VIDE ─── */
        .abonnements-empty {
          text-align: center;
          padding: 40px 20px;
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.06);
        }

        .abonnements-empty i {
          font-size: 48px;
          color: var(--h);
          opacity: 0.4;
        }

        .abonnements-empty h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 18px;
          font-weight: 600;
          color: var(--m);
          margin: 12px 0 4px;
        }

        .abonnements-empty p {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          color: var(--h);
          margin: 0 0 16px;
        }

        .abonnements-empty .btn {
          background: var(--pr);
          border: none;
          border-radius: 30px;
          padding: 10px 24px;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: #fff;
          display: inline-flex;
          align-items: center;
          transition: opacity 0.2s;
        }

        .abonnements-empty .btn:hover {
          opacity: 0.9;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 480px) {
          .abonnements-header {
            padding: 44px 16px 32px;
          }
          .abonnements-header h1 {
            font-size: 19px;
          }
          .abonnement-card .card-body .actions {
            flex-direction: column;
          }
          .abonnement-card .card-header {
            flex-direction: column;
            align-items: stretch;
          }
          .abonnement-card .card-header .status {
            align-self: flex-start;
          }
        }
      `}</style>

      <div className="abonnements-page">
        {/* Header — TOUJOURS visible (loading, erreur ou contenu) */}
        <div className="abonnements-header">
          <button className="menu-btn" onClick={openDrawer} aria-label="Ouvrir le menu">
            <i className="ti ti-menu-2" />
          </button>

          <div className="icon-wrapper">
            <i className="ti ti-credit-card" />
          </div>
          <h1>Mes abonnements</h1>
          <p>Consultez et renouvelez vos abonnements</p>
        </div>

        {/* Contenu */}
        <div className="abonnements-list">
          {loading ? (
            /* ── ÉTAT CHARGEMENT ── */
            <div className="abonnements-loading">
              <div className="spinner"></div>
              <span>Chargement des abonnements…</span>
            </div>
          ) : error ? (
            /* ── ÉTAT ERREUR (identique à PANotifications) ── */
            <div className="abonnements-error">
              <i className="ti ti-alert-circle alert-icon" />
              <h2>Erreur de chargement</h2>
              <p>{error}</p>
              <div className="actions-row">
                <button
                  className="btn"
                  onClick={() => {
                    hasFetchedRef.current = false; // ⬅️ autorise un nouveau fetch
                    loadAbonnements();
                  }}
                >
                  <i className="ti ti-refresh" />
                  Réessayer
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(`/${slug}/parent/accueil`)}
                >
                  <i className="ti ti-home" />
                  Accueil
                </button>
              </div>
            </div>
          ) : abonnements.length === 0 ? (
            /* ── ÉTAT VIDE ── */
            <div className="abonnements-empty">
              <i className="ti ti-credit-card" />
              <h2>Aucun abonnement</h2>
              <p>Vous n'avez pas encore d'abonnement actif</p>
              <button className="btn" onClick={() => navigate(`/${slug}/parent/offres`)}>
                <i className="ti ti-shopping-bag" style={{ marginRight: 6 }} />
                Voir les offres
              </button>
            </div>
          ) : (
            /* ── LISTE DES ABONNEMENTS ── */
            abonnements.map((abonnement, index) => {
              const isActive = abonnement.is_active ?? false;
              const offre = abonnement.offre || {};
              const offreNom = offre.nom || "N/A";
              const offrePrix = offre.prix || 0;
              const offreDuree = offre.duree_jours || 0;

              const eleveNom = abonnement.eleve_nom || "";
              const elevePrenom = abonnement.eleve_prenom || "";
              const eleveMatricule = abonnement.eleve_matricule || "";
              const eleveClasse = abonnement.classe_nom || "";

              const isWorking = actionLoading === abonnement.id;

              return (
                <div
                  key={abonnement.id}
                  className="abonnement-card"
                  style={{ animationDelay: `${index * 0.06}s` }}
                >
                  <div className="card-header">
                    <div className="left">
                      <h3 className="title">
                        <i className="ti ti-star" />
                        Offre : {offreNom}
                      </h3>

                      {elevePrenom && eleveNom && (
                        <div className="eleve-info">
                          <i className="ti ti-user" />
                          <span>
                            {elevePrenom} {eleveNom}
                            {eleveMatricule && (
                              <span style={{ color: "var(--h)", marginLeft: 4 }}>
                                ({eleveMatricule})
                              </span>
                            )}
                          </span>
                          {eleveClasse && (
                            <span className="classe-badge">
                              <i className="ti ti-school" />
                              {eleveClasse}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <span className={`status ${isActive ? 'active' : 'expired'}`}>
                      {isActive ? "Actif" : "Expiré"}
                    </span>
                  </div>

                  <hr className="card-divider" />

                  <div className="card-body">
                    <div className="dates">
                      <span className="item">
                        <i className="ti ti-calendar" />
                        Début : {abonnement.date_debut || "N/A"}
                      </span>
                      <span className="item">
                        <i className="ti ti-calendar" />
                        Fin : {abonnement.date_fin || "N/A"}
                      </span>
                    </div>

                    <p className="description">{offre.description || "Aucune description"}</p>

                    <div className="meta">
                      <span>Prix : {offrePrix.toLocaleString()} FCFA</span>
                      <span>Durée : {offreDuree} jours</span>
                    </div>

                    {!isActive && (
                      <div className="expired-msg">
                        <i className="ti ti-alert-triangle" />
                        Abonnement expiré. Renouvelez pour continuer.
                      </div>
                    )}

                    {!ACTIONS_ENABLED && (
                      <div className="disabled-note">
                        <i className="ti ti-info-circle" />
                        Gestion en ligne bientôt disponible — contactez l'administration.
                      </div>
                    )}

                    <div className="actions">
                      <button
                        className="btn btn-renew"
                        onClick={() => handleRenouveler(abonnement)}
                        disabled={!ACTIONS_ENABLED || isWorking}
                        title={!ACTIONS_ENABLED ? "Fonctionnalité bientôt disponible" : undefined}
                      >
                        {isWorking ? (
                          <span className="btn-spinner" />
                        ) : (
                          <i className="ti ti-refresh" />
                        )}
                        {isActive ? "Renouveler" : "Réactiver"}
                      </button>
                      <button
                        className="btn btn-resilier"
                        onClick={() => handleResilier(abonnement)}
                        disabled={!ACTIONS_ENABLED || isWorking}
                        title={!ACTIONS_ENABLED ? "Fonctionnalité bientôt disponible" : undefined}
                      >
                        {isWorking ? (
                          <span className="btn-spinner" />
                        ) : (
                          <i className="ti ti-trash" />
                        )}
                        Résilier
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}